import React, { useMemo, useState, useEffect } from "react";
import {
  Tabs,
  Tab,
  Button,
  IconButton,
  Popover,
  MenuItem,
} from "@mui/material";
import { DataGrid, GridMoreVertIcon } from "@mui/x-data-grid";
import { gridClasses } from "@mui/x-data-grid/constants";
import { TbFilterEdit } from "react-icons/tb";
import DateTimeModal from "../../Schedule/ScheduleListAdd/DateTimeModal";
import dayjs from "dayjs";
import { HttpService } from "../../../service/HttpService";
import FullScreenLoader from "../../../components/Loader/FullScreenLoader";
import PlantRankingModal from "../../../components/Schedule/fullSwap/PlantRankingModal";
import RoomLogMasterTable from "../../../components/RoomLogMaster/RoomLogMasterTable";
import { tr } from "date-fns/locale";

const FertigationHistory = ({ roomId: externalRoomId }) => {
  const [roomTab, setRoomTab] = useState(0);
  const [phaseTab, setPhaseTab] = useState(0);
  const [epicenterTab, setEpicenterTab] = useState(0);
  const [isPhaseTabManuallySelected, setIsPhaseTabManuallySelected] =
    useState(false);
  const [page, setPage] = useState(0);
  const [rooms, setRooms] = useState([]);
  const [roomsLoading, setRoomsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [growthPhases, setGrowthPhases] = useState([]);
  const [searchTop, setSearchTop] = useState("");
  const [searchBottom, setSearchBottom] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuRowId, setMenuRowId] = useState(null);
  const [plantRankingOpen, setPlantRankingOpen] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [topTableRows, setTopTableRows] = useState([]);
  const [topTableColumns, setTopTableColumns] = useState([]);

  // Separate modal + date filter states for top and bottom tables
  const [openTopDateModal, setOpenTopDateModal] = useState(false);
  const [openBottomDateModal, setOpenBottomDateModal] = useState(false);

  const dateFilterTop = JSON.parse(
    localStorage.getItem("fertigationDateFilterTop"),
  );
  const dateFilterBottom = JSON.parse(
    localStorage.getItem("fertigationDateFilterBottom"),
  );

  const [startDateTop, setStartDateTop] = useState(dateFilterTop?.startDate);
  const [endDateTop, setEndDateTop] = useState(dateFilterTop?.endDate);
  const [selectedRadioTop, setSelectedRadioTop] = useState(
    dateFilterTop?.selectedRadio,
  );
  const [tempStartDateTop, setTempStartDateTop] = useState(
    dateFilterTop?.startDate,
  );
  const [tempEndDateTop, setTempEndDateTop] = useState(dateFilterTop?.endDate);
  const [tempSelectedRadioTop, setTempSelectedRadioTop] = useState(
    dateFilterTop?.selectedRadio,
  );

  const [startDateBottom, setStartDateBottom] = useState(
    dateFilterBottom?.startDate,
  );
  const [endDateBottom, setEndDateBottom] = useState(dateFilterBottom?.endDate);
  const [selectedRadioBottom, setSelectedRadioBottom] = useState(
    dateFilterBottom?.selectedRadio,
  );
  const [tempStartDateBottom, setTempStartDateBottom] = useState(
    dateFilterBottom?.startDate,
  );
  const [tempEndDateBottom, setTempEndDateBottom] = useState(
    dateFilterBottom?.endDate,
  );
  const [tempSelectedRadioBottom, setTempSelectedRadioBottom] = useState(
    dateFilterBottom?.selectedRadio,
  );

  // mapping of tab index → epicenter value from backend
  const tabTypes = ["E1", "E2", "E3"];

  // Fetch rooms for room tabs
  const fetchRooms = async () => {
    setRoomsLoading(true);
    try {
      const httpService = new HttpService();
      const res = await httpService.get("rooms?sortBy=name&sortOrder=ASC");

      const roomsData = res?.data?.data || [];
      // industry-standard: configurable special rooms
      const EXTRA_ROOMS = ["clone room", "mom room"];
      const filteredRooms = roomsData
        // .filter(
        //   (room) => room.name && /^room\s([1-9][0-9]?)$/i.test(room.name.trim())
        // )
        .filter((room) => {
          if (!room.name) return false;

          const name = room.name.trim().toLowerCase();

          // existing logic (unchanged)
          const isNumberedRoom = /^room\s([1-9][0-9]?)$/.test(name);

          // minimal addition
          const isExtraRoom = EXTRA_ROOMS.includes(name);

          return isNumberedRoom || isExtraRoom;
        })
        // .sort((a, b) => {
        //   const numA = parseInt(a.name.match(/\d+/)?.[0] || 0, 10);
        //   const numB = parseInt(b.name.match(/\d+/)?.[0] || 0, 10);
        //   return numA - numB;
        // });
        .sort((a, b) => {
          const getNumber = (name) => {
            const match = name.match(/\d+/);
            return match ? Number(match[0]) : Infinity;
          };

          return getNumber(a.name) - getNumber(b.name);
        });

      setRooms(filteredRooms);
      setRoomTab(0);
    } catch (e) {
      console.error("Error fetching rooms:", e);
      setRooms([]);
    }
    setRoomsLoading(false);
  };

  const handlePlantRankingSuccess = async (rowId, overallGrowth) => {
    const httpService = new HttpService();
    const growthPhaseId = growthPhases[phaseTab]?.id;
    await httpService.put(`mapping/overall-growth/${rowId}`, {
      overall_growth: overallGrowth,
      growth_phase_id: growthPhaseId,
    });
    // Update local state after successful API call
    setRows((prevRows) =>
      prevRows.map((row) =>
        row.id === rowId ? { ...row, overall_growth: overallGrowth } : row,
      ),
    );
    // Refresh the data to ensure consistency with server
    fetchMappings();
  };

  const fetchMappings = async () => {
    setLoading(true);
    try {
      const httpService = new HttpService();
      let apiUrl = `mapping?search=${encodeURIComponent(searchBottom || "")}`;
      apiUrl += `&epicenter=${tabTypes[epicenterTab]}`;
      if (externalRoomId) {
        apiUrl += `&room_id=${externalRoomId}`;
      } else if (rooms.length && rooms[roomTab]) {
        apiUrl += `&room_id=${rooms[roomTab]?.id}`;
      }
      if (growthPhases.length && growthPhases[phaseTab]) {
        apiUrl += `&growth_phase_id=${growthPhases[phaseTab]?.id}`;
      }
      if (
        startDateBottom !== undefined &&
        startDateBottom !== null &&
        endDateBottom !== undefined &&
        endDateBottom !== null &&
        startDateBottom !== "" &&
        endDateBottom !== ""
      ) {
        const formattedStart = dayjs(startDateBottom).format("MM-DD-YYYY");
        const formattedEnd = endDateBottom
          ? dayjs(endDateBottom).format("MM-DD-YYYY")
          : dayjs(startDateBottom).format("MM-DD-YYYY");
        apiUrl = `${apiUrl}&start_date=${formattedStart}&end_date=${formattedEnd}`;
      }

      const res = await httpService.get(apiUrl);
      const list = Array.isArray(res?.data?.data)
        ? res.data.data
        : Array.isArray(res?.data?.data)
          ? res.data?.data
          : [];
      const growthPhaseId = res?.data?.growthPhase?.growth_phase_id
        ? res?.data?.growthPhase?.growth_phase_id
        : 0;
      const pagination = res?.data?.pagination;
      const matchedPhase = growthPhases.find(
        (phase) => String(phase.id) === String(growthPhaseId),
      );

      const normalized = list.map((it, idx) => ({
        id: it.id ?? idx,
        metrc_id: it.matric_id || it.metrc_id || it.clone_id || "",
        bucket_id: it.bucket_id || it.bucket || "",
        // clone_id: it.clone_id || "",
        // date_of_mapping: it.date_of_mapping || it.mapping_date || it.date || "",
        // employee_name: it.employee_name || "",
        planting_date: it.planting_date || it.date_of_planting || "",
        // epicenter: it.epicenter || "",
        // room_id: it.room_id || "",
        // room_name: it.room_name || "",
        // root_ranking: it.root_ranking || "-",
        // health_ranking: it.health_ranking || "-",
        // development_ranking: it.development_ranking || "-",
        cultivar_name: it.cultivar_name || "",
        overall_growth: it.overall_growth || "-",
      }));

      if (matchedPhase && !isPhaseTabManuallySelected) {
        const phaseIndex = growthPhases.findIndex(
          (phase) => String(phase.id) === String(matchedPhase.id),
        );
        if (phaseIndex !== -1) {
          setPhaseTab(phaseIndex);
        }
      } else if (!matchedPhase && !isPhaseTabManuallySelected) {
        setPhaseTab(0);
      }
      setRows(normalized);
      setRowCount(pagination?.total);
    } catch (e) {
      setRows([]);
      setRowCount(0);
    }
    setLoading(false);
  };

  const fetchGrowthPhases = async () => {
    try {
      const httpService = new HttpService();
      const response = await httpService.get("growth-phase/get-growth-phase");
      setGrowthPhases(response.data || []);
    } catch (error) {
      console.error("Error fetching growth phases:", error);
      setGrowthPhases([]);
    }
  };

  const FIXED_ORDER = ["PPM (500 Scale)", "EC", "Target PH", "Solution Temp"];

  const fetchFertigationHistory = async () => {
    setLoading(true);
    setTopTableRows([]);
    try {
      const httpService = new HttpService();
      let apiUrl = "task/fertigation-history?";

      apiUrl += `epicenter=${tabTypes[epicenterTab] || ""}`;

      if (externalRoomId) {
        apiUrl += `&room_id=${externalRoomId}`;
      } else if (rooms.length && rooms[roomTab]) {
        apiUrl += `&room_id=${rooms[roomTab]?.id}`;
      }

      if (growthPhases.length && growthPhases[phaseTab]) {
        apiUrl += `&growth_phase_id=${growthPhases[phaseTab]?.id}`;
      }

      if (startDateTop && endDateTop) {
        const formattedStart = dayjs(startDateTop).format("MM-DD-YYYY");
        const formattedEnd = dayjs(endDateTop).format("MM-DD-YYYY");
        apiUrl += `&startDate=${formattedStart}&endDate=${formattedEnd}`;
      }

      const res = await httpService.get(apiUrl);
      const list = Array.isArray(res?.data) ? res.data : [];
      const baseColumns = [
        {
          field: "fertigation_date",
          headerName: "Fertigation Date",
          flex: 1,
          minWidth: 200,
        },
        {
          field: "task_type",
          headerName: "Task Type",
          flex: 1,
          minWidth: 150,
        },
        {
          field: "recipe_name",
          headerName: "Recipe Name",
          flex: 1,
          minWidth: 150,
        },
      ];
      setTopTableColumns(baseColumns);
      if (list.length > 0) {
        const firstItem = list[0];
        const nutrientKeys = Object.keys(firstItem.nutrients || {});
        const addBacksKeys = Object.keys(firstItem.addbacks || {});

        const orderedAddBacksKeys = [
          ...FIXED_ORDER.filter((key) => addBacksKeys.includes(key)),
          ...addBacksKeys
            .filter((key) => !FIXED_ORDER.includes(key))
            .sort((a, b) => a.localeCompare(b)),
        ];

        const dynamicColumns = [
          ...baseColumns,
          ...orderedAddBacksKeys.map((key) => ({
            field: key.toLowerCase().replace(/\s+/g, "_").replace(/[()]/g, ""),
            headerName: key,
            flex: 1,
            minWidth: 150,
          })),
          ...nutrientKeys
            .slice() // safe copy (prevents mutation)
            .sort((a, b) => a.localeCompare(b))
            .map((key) => ({
              field: key
                .toLowerCase()
                .replace(/\s+/g, "_")
                .replace(/[()]/g, ""),
              headerName: key,
              flex: 1,
              minWidth: 150,
            })),
          { field: "emp_name", headerName: "EMP Name", flex: 1, minWidth: 160 },
        ];
        setTopTableColumns(dynamicColumns);
        // setTopTableColumns([
        //   ...dynamicColumns,
        //   ...dynamicAddBackColumns,
        //   ...dynamicNutrientColumns,
        //   { field: "emp_name", headerName: "EMP Name", flex: 1, minWidth: 160 },
        // ]);
      }

      const normalized = list.map((it, idx) => {
        const row = {
          id: `fertigation-${it.task_full_swap_id}`,
          fertigation_date: it.fertigation_date
            ? dayjs(it.fertigation_date).format("MM-DD-YYYY")
            : "",
          task_type: it.type || "",
          emp_name: it.emp_name || "",
          recipe_name: it.recipe_name || "",
        };
        if (it.addbacks) {
          Object.keys(it.addbacks).forEach((key) => {
            const fieldName = key
              .toLowerCase()
              .replace(/\s+/g, "_")
              .replace(/[()]/g, "");
            const value = it.addbacks[key];
            row[fieldName] =
              value === null ||
              value === undefined ||
              value === 0 ||
              value === ""
                ? "-"
                : value;
          });
        }
        if (it.nutrients) {
          Object.keys(it.nutrients).forEach((key) => {
            const fieldName = key
              .toLowerCase()
              .replace(/\s+/g, "_")
              .replace(/[()]/g, "");
            const value = it.nutrients[key];
            row[fieldName] =
              value === null ||
              value === undefined ||
              value === 0 ||
              value === ""
                ? "-"
                : value;
          });
        }

        return row;
      });
      setTopTableRows(normalized);
    } catch (e) {
      console.error("Error:", e);
      setTopTableRows([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    const dateFilterTop = JSON.parse(
      localStorage.getItem("fertigationDateFilterTop"),
    );
    const dateFilterBottom = JSON.parse(
      localStorage.getItem("fertigationDateFilterBottom"),
    );

    if (dateFilterTop && dateFilterTop?.selectedRadio) {
      setStartDateTop(dateFilterTop?.startDate);
      setEndDateTop(dateFilterTop?.endDate);
      setSelectedRadioTop(dateFilterTop?.selectedRadio);
    }

    if (dateFilterBottom && dateFilterBottom?.selectedRadio) {
      setStartDateBottom(dateFilterBottom?.startDate);
      setEndDateBottom(dateFilterBottom?.endDate);
      setSelectedRadioBottom(dateFilterBottom?.selectedRadio);
    }
  }, []);

  useEffect(() => {
    if (!externalRoomId) {
      fetchRooms();
    }
    fetchGrowthPhases();
  }, [externalRoomId]);

  useEffect(() => {
    if (externalRoomId || rooms.length > 0) {
      fetchMappings();
      fetchFertigationHistory();
    }
  }, [
    rooms,
    externalRoomId,
    epicenterTab,
    roomTab,
    phaseTab,
    searchBottom,
    startDateBottom,
    endDateBottom,
    startDateTop,
    endDateTop,
  ]);

  const epicenters = [
    "Epicenter 1",
    "Epicenter 2",
    "Epicenter 3",
    // "Room Log Master",
  ];

  const commonTabsSx = {
    backgroundColor: "#fffffc",
    "& .MuiTabs-indicator": {
      backgroundColor: "#00A272",
      height: "3px",
    },
    "& .MuiTab-root": {
      position: "relative",
      color: "#A1A2A1",
      fontWeight: 400,
      fontSize: "16px",
      textTransform: "none",
      padding: "12px 20px",
      marginRight: "8px",
      "&:not(.Mui-selected)::after": {
        content: '""',
        position: "absolute",
        left: 0,
        right: 0,
        bottom: 0,
        height: "3px",
        backgroundColor: "#C4C4C4",
      },
      "&.Mui-selected": {
        color: "#00A272 !important",
        fontWeight: 600,
        backgroundColor: "#E6F6F1",
        borderRadius: "4px 4px 0 0",
      },
    },
  };

  const topRows = useMemo(() => topTableRows, [topTableRows]);
  const topColumns = useMemo(
    () =>
      topTableColumns.length > 0
        ? topTableColumns
        : [
            {
              field: "fertigation_date",
              headerName: "Fertigation Date",
              flex: 1,
              minWidth: 200,
            },
            {
              field: "task_type",
              headerName: "Task Type",
              flex: 1,
              minWidth: 150,
            },
            {
              field: "emp_name",
              headerName: "EMP Name",
              flex: 1,
              minWidth: 160,
            },
          ],
    [topTableColumns],
  );

  // API data for bottom table (plant details)
  const bottomRows = useMemo(() => rows, [rows]);

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuRowId(null);
  };

  const bottomColumns = [
    {
      field: "action",
      headerName: "Action",
      headerAlign: "center",
      align: "center",
      sortable: false,
      width: 160,
      renderCell: (params) => (
        <>
          <IconButton
            onClick={(e) => {
              handleMenuClick(e);
              setMenuRowId(params?.row?.id);
            }}
            sx={{ padding: 0 }}
          >
            <GridMoreVertIcon />
          </IconButton>
          {anchorEl && menuRowId === params?.row?.id && (
            <Popover
              className="popup-menu"
              open={Boolean(anchorEl)}
              anchorEl={anchorEl}
              onClose={handleCloseMenu}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <MenuItem
                onClick={() => {
                  setSelectedRowData(params?.row);
                  setPlantRankingOpen(true);
                  handleCloseMenu();
                }}
                className="menu-list-items"
              >
                <div className="menu-list-items-text">Plant ranking</div>
              </MenuItem>
            </Popover>
          )}
        </>
      ),
    },
    {
      field: "metrc_id",
      headerName: "Metric ID",
      flex: 1.3,
      minWidth: 230,
    },
    {
      field: "bucket_id",
      headerName: "Bucket ID",
      flex: 1,
      minWidth: 150,
    },
    // {
    //   field: "clone_id",
    //   headerName: "Clone ID",
    //   flex: 1,
    //   minWidth: 160,
    // },
    {
      field: "cultivar_name",
      headerName: "Cultivar Name",
      flex: 1,
      minWidth: 180,
    },
    {
      field: "planting_date",
      headerName: "Planting Date",
      flex: 1,
      minWidth: 180,
      renderCell: (params) => (
        <div>
          {params.row?.planting_date
            ? dayjs(params.row.planting_date).format("MM-DD-YYYY")
            : "-"}
        </div>
      ),
    },
    {
      field: "overall_growth",
      headerName: "Overall Growth",
      flex: 1,
      minWidth: 190,
    },
    // {
    //   field: "root_ranking",
    //   headerName: "Roots Rating",
    //   flex: 1,
    //   minWidth: 150,
    // },
  ];

  // Filter top rows based on search and date
  const filteredTopRows = useMemo(() => {
    let filtered = topRows;

    if (searchTop) {
      filtered = filtered.filter((row) =>
        Object.values(row).some((value) =>
          String(value).toLowerCase().includes(searchTop.toLowerCase()),
        ),
      );
    }

    if (startDateTop && endDateTop) {
      filtered = filtered.filter((row) => {
        const rowDate = dayjs(row.fertigation_date, "MM-DD-YYYY");
        const start = dayjs(startDateTop);
        const end = dayjs(endDateTop);

        if (!rowDate.isValid() || !start.isValid() || !end.isValid()) {
          return true;
        }

        return rowDate.isBetween(start, end, "day", "[]");
      });
    }

    return filtered;
  }, [topRows, searchTop, startDateTop, endDateTop]);

  const filteredBottomRows = bottomRows;

  return (
    <>
      {(loading || roomsLoading) && <FullScreenLoader />}
      <div className="company">
        {!externalRoomId && (
          <div className="page-title">
            <div className="title-wrap">
              <h1>Fertigation History</h1>
            </div>
          </div>
        )}

        <div className="common_content">
          {!externalRoomId && (
            <Tabs
              value={roomTab}
              onChange={(e, newValue) => {
                setRoomTab(newValue);
                setPage(0);
                setIsPhaseTabManuallySelected(false);
              }}
              variant="scrollable"
              scrollButtons="false"
              sx={{
                backgroundColor: "#fffffc",
                ".MuiTabs-flexContainer": {
                  display: "flex",
                },
                "& .MuiTabs-indicator": {
                  backgroundColor: "#00A272",
                  height: "3px",
                },
                "& .MuiTab-root": {
                  position: "relative",
                  color: "#A1A2A1",
                  fontWeight: 400,
                  fontSize: "16px",
                  textTransform: "none",
                  padding: "12px 20px",
                  marginRight: "8px",
                  "&:not(.Mui-selected)::after": {
                    content: '""',
                    position: "absolute",
                    left: 0,
                    right: 0,
                    bottom: 0,
                    height: "3px",
                    backgroundColor: "#C4C4C4",
                  },
                  "&.Mui-selected": {
                    color: "#00A272 !important",
                    fontWeight: 600,
                    backgroundColor: "#E6F6F1",
                    borderRadius: "4px 4px 0 0",
                  },
                },
                mb: 2,
              }}
            >
              {rooms.map((room) => (
                <Tab key={room.id} label={room.name} />
              ))}
            </Tabs>
          )}

          {/* Growth phase row */}
          <Tabs
            value={phaseTab}
            onChange={(_, newValue) => {
              if (epicenterTab !== 3) {
                setPhaseTab(newValue);
                setIsPhaseTabManuallySelected(true);
              }
            }}
            variant="scrollable"
            scrollButtons={false}
            sx={{
              ...(epicenterTab !== 3 ? commonTabsSx : {}),
              backgroundColor: "#fffffc",
              ".MuiTabs-flexContainer": {
                display: "flex",
                justifyContent: "flex-start",
              },
              mb: 2,
              ...(epicenterTab === 3 && {
                pointerEvents: "none",
                opacity: 0.5,
                "& .MuiTabs-indicator": {
                  display: "none",
                },
                "& .MuiTab-root": {
                  color: "#A1A2A1 !important",
                  cursor: "not-allowed",
                  fontWeight: 400,
                  fontSize: "16px",
                  textTransform: "none",
                  padding: "12px 20px",
                  marginRight: "8px",
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    left: 0,
                    right: 0,
                    bottom: 0,
                    height: "3px",
                    backgroundColor: "#C4C4C4",
                  },
                },
              }),
            }}
          >
            {growthPhases.map((phase) => (
              <Tab
                key={phase.id}
                label={phase.name}
                disabled={epicenterTab === 3}
              />
            ))}
          </Tabs>

          {/* Epicenter row */}
          <Tabs
            value={epicenterTab}
            onChange={(_, newValue) => setEpicenterTab(newValue)}
            variant="scrollable"
            scrollButtons={false}
            sx={{
              ...commonTabsSx,
              ".MuiTabs-flexContainer": {
                display: "flex",
                justifyContent: "flex-start",
              },
              mb: 3,
            }}
          >
            {epicenters.map((epi) => (
              <Tab key={epi} label={epi} />
            ))}
          </Tabs>

          {/* Top table: Fertigation events */}
          {epicenterTab === 3 ? (
            rooms.length > 0 ? (
              <RoomLogMasterTable
                loading={loading}
                roomId={rooms[roomTab]?.id || rooms[0]?.id || 0}
              />
            ) : (
              <div style={{ textAlign: "center", padding: "20px" }}>
                Loading rooms...
              </div>
            )
          ) : (
            <>
              <div className="table-filter-wrap d-flex justify-content-between pb-0 pt-2">
                <div className="header_search-wrap">
                  <div className="header_search_bar common_content">
                    <div className="input-group input_group">
                      <button className="btn search-btn search_btn">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="15"
                          height="15"
                          viewBox="0 0 21 21"
                          fill="none"
                        >
                          <circle
                            cx="9.98856"
                            cy="9.98856"
                            r="8.98856"
                            stroke="#495057"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M16.2402 16.7071L19.7643 20.222"
                            stroke="#495057"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                      <input
                        type="search"
                        className="form-control form_control"
                        placeholder="Search..."
                        aria-label="Search fertigation"
                        onChange={(e) => setSearchTop(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
                <Button
                  variant="outlined"
                  color="primary"
                  className="filters-btn"
                  style={{ textTransform: "none" }}
                  onClick={() => setOpenTopDateModal(true)}
                >
                  <TbFilterEdit /> Filters
                </Button>

                {openTopDateModal && (
                  <DateTimeModal
                    handleDateTimeModal={() => setOpenTopDateModal(false)}
                    setStartDate={(e) => setTempStartDateTop(e)}
                    setEndDate={(e) => setTempEndDateTop(e)}
                    open={openTopDateModal}
                    fetchSchedule={() => {
                      setStartDateTop(tempStartDateTop);
                      setEndDateTop(tempEndDateTop);
                      setSelectedRadioTop(tempSelectedRadioTop);
                      const fertigationDateFilterTop = {
                        startDate: tempStartDateTop,
                        endDate: tempEndDateTop,
                        selectedRadio: tempSelectedRadioTop,
                      };
                      localStorage.setItem(
                        "fertigationDateFilterTop",
                        JSON.stringify(fertigationDateFilterTop),
                      );
                    }}
                    selectedRadio={tempSelectedRadioTop}
                    setSelectedRadio={(e) => setTempSelectedRadioTop(e)}
                    startDate={tempStartDateTop}
                    endDate={tempEndDateTop}
                  />
                )}
              </div>

              <div
                style={{
                  height: `${filteredTopRows?.length > 0 ? "" : "200px"}`,
                  width: "100%",
                  marginTop: 12,
                }}
              >
                <DataGrid
                  rows={filteredTopRows}
                  columns={topColumns}
                  getRowHeight={() => null}
                  getRowClassName={(params) =>
                    params.row.task_type === "Add Back" ? "add-back-row" : ""
                  }
                  sx={{
                    border: "1px solid #E0E0E0",
                    [`& .${gridClasses.cell}`]: { p: 1.8 },
                    "& .add-back-row": {
                      backgroundColor: "#ffd3ac59 !important",
                      "&:hover": {
                        backgroundColor: "#ffd3ac70 !important",
                      },
                    },
                  }}
                  autoHeight={filteredTopRows.length > 0}
                  showCellVerticalBorder
                  showColumnVerticalBorder
                  pageSizeOptions={[10, 25, 50]}
                  hideFooter={filteredTopRows?.length === 0}
                />
              </div>
            </>
          )}

          {/* Bottom table: Plant details */}
          {epicenterTab !== 3 && (
            <>
              <div className="table-filter-wrap d-flex justify-content-between pb-0 pt-2 mt-4">
                <div className="header_search-wrap">
                  <div className="header_search_bar common_content">
                    <div className="input-group input_group">
                      <button className="btn search-btn search_btn">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="10"
                          height="10"
                          viewBox="0 0 21 21"
                          fill="none"
                        >
                          <circle
                            cx="9.98856"
                            cy="9.98856"
                            r="8.98856"
                            stroke="#495057"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M16.2402 16.7071L19.7643 20.222"
                            stroke="#495057"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                      <input
                        type="search"
                        className="form-control form_control"
                        placeholder="Search..."
                        aria-label="Search plants"
                        onChange={(e) => setSearchBottom(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
                <Button
                  variant="outlined"
                  color="primary"
                  className="filters-btn"
                  style={{ textTransform: "none" }}
                  onClick={() => setOpenBottomDateModal(true)}
                >
                  <TbFilterEdit /> Filters
                </Button>

                {openBottomDateModal && (
                  <DateTimeModal
                    handleDateTimeModal={() => setOpenBottomDateModal(false)}
                    setStartDate={(e) => setTempStartDateBottom(e)}
                    setEndDate={(e) => setTempEndDateBottom(e)}
                    open={openBottomDateModal}
                    fetchSchedule={() => {
                      setStartDateBottom(tempStartDateBottom);
                      setEndDateBottom(tempEndDateBottom);
                      setSelectedRadioBottom(tempSelectedRadioBottom);
                      const fertigationDateFilterBottom = {
                        startDate: tempStartDateBottom,
                        endDate: tempEndDateBottom,
                        selectedRadio: tempSelectedRadioBottom,
                      };
                      localStorage.setItem(
                        "fertigationDateFilterBottom",
                        JSON.stringify(fertigationDateFilterBottom),
                      );
                    }}
                    selectedRadio={tempSelectedRadioBottom}
                    setSelectedRadio={(e) => setTempSelectedRadioBottom(e)}
                    startDate={tempStartDateBottom}
                    endDate={tempEndDateBottom}
                  />
                )}
              </div>

              <div
                style={{
                  height: `${filteredBottomRows?.length > 0 ? "" : "200px"}`,
                  width: "100%",
                  marginTop: 12,
                }}
              >
                <DataGrid
                  rows={filteredBottomRows}
                  columns={bottomColumns}
                  getRowHeight={() => "auto"}
                  sx={{
                    border: "1px solid #E0E0E0",
                    [`& .${gridClasses.cell}`]: { p: 1.8 },
                  }}
                  autoHeight
                  showCellVerticalBorder
                  showColumnVerticalBorder
                  pageSizeOptions={[10, 25, 50]}
                />
              </div>
            </>
          )}
        </div>
      </div>

      <PlantRankingModal
        open={plantRankingOpen}
        onClose={() => setPlantRankingOpen(false)}
        rowData={selectedRowData}
        onSuccess={handlePlantRankingSuccess}
      />
    </>
  );
};

export default FertigationHistory;
